import { RdsBadgeColor } from '@rds/core';

import {
  BookingRequestBaseApiModel,
  BookingRequestCreateApiRequestModel,
  BookingRequestDetailsApiResponseModel,
  BookingRequestRedirectApiModel,
  BookingRequestUpdateApiRequestModel,
  BookingStatusApiResponseEnum,
  ChatMessageApiResponseModel,
  ChatMessageTypeApiResponseEnum,
  Requester,
  RoomServicesApiResponseEnum,
} from './booking-request-api.model';
import { RoleType } from '../../auth';
import { NotificationSourceTypeEnum } from '../../notifications';
import { RoomDetailsModel } from '../../rooms';

export interface RoomBookingModel {
  servicesBadge?: {
    value: RoomServicesEnum;
    text: string;
    color: RdsBadgeColor;
    url?: string;
  }[];
  createTime: string;
  id: number;
  roomDescription: string;
  roomDexId: string;
  roomName: string;
  siteName: string;
  buildingName: string;
  floorName: string;
  startDate: Date;
  endDate: Date;
  startTime: string;
  eventName: string;
  endTime: string;
  status: {
    value: BookingStatusEnum;
    text: string;
    color: RdsBadgeColor;
  };
  services: {
    id: number;
    serviceType: RoomServicesEnum;
    url?: string;
    ticketName?: string;
    ticketStatus?: string;
  }[];
  requester: RequesterModel;
  onBehalfParticipants?: RequesterModel[];
}

export interface BookingRequestDetailsViewModel {
  bookingDetails: BookingRequestDetailsModel;
  roomDetails: RoomDetailsModel;
}

export interface BookingRequestDetailsModel
  extends Omit<
    BookingRequestDetailsApiResponseModel,
    'startTime' | 'endTime' | 'status' | 'createTime'
  > {
  createTime: Date;
  startTime: Date;
  status: {
    value: BookingStatusEnum;
    text: string;
    color: RdsBadgeColor;
  };
  endTime: Date;
}

export type BookingRequestDetailsUpdateModel =
  BookingRequestUpdateApiRequestModel;

export type BookingRequestCreateModel = BookingRequestCreateApiRequestModel;

export type BookingRequestUpdateModel = BookingRequestUpdateApiRequestModel;

export type BookingRequestBaseModel = BookingRequestBaseApiModel;

export type ChatMessageModel = ChatMessageApiResponseModel;

export type ChatMessageTypeEnum = ChatMessageTypeApiResponseEnum;
export const ChatMessageTypeEnum = { ...ChatMessageTypeApiResponseEnum };

export type RoomServicesEnum = RoomServicesApiResponseEnum;
export const RoomServicesEnum = { ...RoomServicesApiResponseEnum };

export type BookingStatusEnum = BookingStatusApiResponseEnum;
export const BookingStatusEnum = { ...BookingStatusApiResponseEnum };

export interface BookingBasicData {
  bookingId: number;
  roomDexId: string;
  setActiveTab?: NotificationSourceTypeEnum;
  role?: RoleType | undefined;
}

export type RequesterModel = Requester;

export type CustomIcon =
  | 'lock'
  | 'furniture'
  | 'catering'
  | 'bookly-short'
  | 'bulb';

export type BookingRequestRedirectDTO = BookingRequestRedirectApiModel;

export interface ServiceViewModel {
  service: RoomServicesApiResponseEnum;
  customIcon: CustomIcon;
  name: string;
  description: string;
  isUrl: boolean;
  ticketName?: string;
  ticketStatus?: string;
}

export interface CalendarData {
  semiPrivate: boolean;
  roomCalendarId?: string;
  startDate?: string | undefined;
}

export const SERVICE_MODEL_ARRAY: Map<
  RoomServicesApiResponseEnum,
  ServiceViewModel
> = new Map([
  [
    RoomServicesApiResponseEnum.Catering,
    {
      service: RoomServicesApiResponseEnum.Catering,
      customIcon: 'catering',
      name: 'Catering Service',
      description: '',
      isUrl: false,
      ticketName: '',
      ticketStatus: '',
    },
  ],
  [
    RoomServicesApiResponseEnum.Locker,
    {
      service: RoomServicesApiResponseEnum.Locker,
      customIcon: 'lock',
      name: 'Room Locking Service',
      description:
        'The request should be send no later than 48 hours in advance of the event and should last longer than 24 hours otherwise it may be rejected.',
      isUrl: false,
      ticketName: '',
      ticketStatus: '',
    },
  ],
  [
    RoomServicesApiResponseEnum.Furniture,
    {
      service: RoomServicesApiResponseEnum.Furniture,
      customIcon: 'furniture',
      name: 'Furniture Service',
      description: '',
      isUrl: false,
      ticketName: '',
      ticketStatus: '',
    },
  ],
]);

export interface BookingHistoryParticipantModel {
  email?: string;
  fullName?: string;
}

/**
 * A change leaf holds either a scalar (serialised as a string by the API) or a
 * list of participants — `onBehalfParticipants` is sent as a whole array on
 * both sides of the change instead of per-index entries.
 */
export type BookingHistoryChangeValueModel =
  | string
  | BookingHistoryParticipantModel[];

export interface BookingHistoryChangeModel {
  previous?: BookingHistoryChangeValueModel;
  current?: BookingHistoryChangeValueModel;
}

export interface BookingHistoryChangesModel {
  [key: string]: BookingHistoryChangeModel | BookingHistoryChangesModel;
}

export interface BookingHistoryEntryModel {
  revisionNumber: number;
  updatedBy: string;
  updateDate: Date;
  changes: BookingHistoryChangesModel;
  bookingStatus: BookingStatusApiResponseEnum;
  isReapproved?: boolean;
}
